import { WorkItem, SnapshotUser } from './snapshot_works';

// Define icon structure (assuming Lucide-like SVG strings or class names)
export type LucideIcon = string; // Placeholder: e.g., '<svg>...</svg>' or 'lucide-briefcase'

export interface WorkTimeBreakdown {
  total: number;
  byType: Record<string, number>;
  byRating: Record<string, number>;
}

export class WorkVisualization {
  private container: HTMLElement;
  private data: WorkTimeBreakdown;
  private teamData: WorkTimeBreakdown;
  private personalMaxMonthlyHours: number; // Will be calculated
  private teamMaxMonthlyHours: number;     // Will be calculated
  private baseMaxMonthlyHoursPerUser: number = 173; // Default, can be made configurable. 40 hours/week * 4.33 weeks/month
  private tooltipElement: HTMLElement | null = null;
  private personalSectionTitle: string;
  private onBarClick: ((type: string, category: 'work_type' | 'rating') => void) | null = null;

  private workTypeOrder: string[] = ['operational', 'internal_meetings', 'external_meetings', 'strategic'];
  private ratingOrder: string[] = ['low', 'medium', 'high'];

  private colors: Record<string, string> = {
    operational: 'bg-blue-500',
    internal_meetings: 'bg-green-500',
    external_meetings: 'bg-yellow-500',
    strategic: 'bg-purple-500',
    low: 'bg-red-500',
    medium: 'bg-gray-500',
    high: 'bg-emerald-500',
    unallocated: 'bg-gray-200 text-gray-700'
  };

  private categoryIcons: Record<string, LucideIcon> = {
    operational: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-briefcase"><rect width="20" height="14" x="2" y="7" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>',
    internal_meetings: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-users"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
    external_meetings: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-user-plus"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" x2="19" y1="8" y2="14"/><line x1="22" x2="16" y1="11" y2="11"/></svg>',
    strategic: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-target"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>',
    low: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trending-down"><polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/><polyline points="16 17 22 17 22 11"/></svg>',
    medium: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-minus"><line x1="5" y1="12" x2="19" y2="12"/></svg>',
    high: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-trending-up"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>',
    unallocated: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-clock"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>'
  };

  constructor(container: HTMLElement, personalSectionTitle: string = "My Work Map", onBarClick?: (type: string, category: 'work_type' | 'rating') => void) {
    this.container = container;
    this.personalSectionTitle = personalSectionTitle;
    this.data = { total: 0, byType: {}, byRating: {} };
    this.teamData = { total: 0, byType: {}, byRating: {} };
    this.onBarClick = onBarClick || null;
    this.renderInitialStructure();
    this.setupTooltip();
  }

  public setContainer(newContainer: HTMLElement): void {
    this.container = newContainer;
    // It's important to re-run parts of initialization that depend on the container,
    // especially if the old container's contents were completely replaced.
    // For this class, renderInitialStructure and setupTooltip build inside this.container.
    // We might need to clear the newContainer first if it's not guaranteed to be empty.
    newContainer.innerHTML = ''; // Clear out any placeholder content
    this.renderInitialStructure();
    this.setupTooltip(); // Re-initialize tooltip within the new container
  }

  private setupTooltip(): void {
    this.tooltipElement = document.createElement('div');
    this.tooltipElement.className = 'absolute z-10 hidden px-3 py-1.5 text-sm font-medium text-white bg-gray-900 rounded-md shadow-sm opacity-90 pointer-events-none';
    this.container.appendChild(this.tooltipElement);
  }

  private renderInitialStructure(): void {
    const createBarSectionHTML = (sectionPrefix: string, title: string, categoryOrder: string[]): string => {
      let barSegmentsHTML = '';
      let legendItemsHTML = '';

      categoryOrder.forEach(key => {
        const colorClass = this.colors[key] || 'bg-gray-300';
        const iconSvg = this.categoryIcons[key] || '';
        const iconColorClass = colorClass.replace('bg-', 'text-');

        barSegmentsHTML += `
          <div id="${sectionPrefix}-${key}-bar" 
               class="${colorClass} h-full flex items-center justify-center text-white bar-segment" 
               data-category-name="${key.replace('_', ' ')}"
               data-color-class="${colorClass}"
               style="width: 0%; transition: width 0.5s ease-in-out;" 
               title="${key}: 0 hours">
            <span class="opacity-75 bar-icon">${iconSvg}</span>
          </div>
        `;
        legendItemsHTML += `
          <div class="flex items-center">
            <div class="w-3 h-3 ${colorClass} rounded mr-1.5 flex items-center justify-center">
              <span class="${iconColorClass} opacity-75 scale-75 legend-icon">${iconSvg.replace(/width="16"/g, 'width="12"').replace(/height="16"/g, 'height="12"')}</span>
            </div>
            <span class="text-xs text-gray-600">${key.replace('_', ' ')} (<span id="${sectionPrefix}-${key}-legend-hours">0</span>h)</span>
          </div>
        `;
      });

      // Unallocated
      const unallocatedColor = this.colors.unallocated.split(' ')[0];
      const unallocatedTextColor = this.colors.unallocated.includes('text-') ? this.colors.unallocated.split(' ').find(c => c.startsWith('text-')) : 'text-gray-700';
      const unallocatedIconSvg = this.categoryIcons.unallocated || '';
      const unallocatedIconLegendColor = unallocatedTextColor;

      barSegmentsHTML += `
        <div id="${sectionPrefix}-unallocated-bar"
             class="${unallocatedColor} h-full flex items-center justify-center ${unallocatedTextColor} bar-segment"
             data-category-name="Unallocated"
             data-color-class="${unallocatedColor} ${unallocatedTextColor}"
             style="width: 100%; transition: width 0.5s ease-in-out;"
             title="Unallocated: ${this.baseMaxMonthlyHoursPerUser} hours">
          <span class="opacity-75 bar-icon">${unallocatedIconSvg}</span>
        </div>
      `;
      legendItemsHTML += `
        <div class="flex items-center">
          <div class="w-3 h-3 ${unallocatedColor} rounded mr-1.5 flex items-center justify-center">
            <span class="${unallocatedIconLegendColor} opacity-75 scale-75 legend-icon">${unallocatedIconSvg.replace(/width="16"/g, 'width="12"').replace(/height="16"/g, 'height="12"')}</span>
          </div>
          <span class="text-xs text-gray-600">Unallocated (<span id="${sectionPrefix}-unallocated-legend-hours">${this.baseMaxMonthlyHoursPerUser}</span>h)</span>
        </div>
      `;

      return `
        <div class="mb-6">
          <div class="flex justify-between items-center mb-2">
            <h3 class="text-sm font-medium text-gray-700">${title}</h3>
            <span class="text-sm text-gray-500"><span id="${sectionPrefix}-total-hours">0</span> hours per month</span>
          </div>
          <div id="${sectionPrefix}-bar-container" class="h-8 flex rounded-full overflow-hidden bg-gray-100">
            ${barSegmentsHTML}
          </div>
          <div id="${sectionPrefix}-legend-container" class="mt-3 flex flex-wrap gap-x-4 gap-y-2">
            ${legendItemsHTML}
          </div>
        </div>
      `;
    };
    
    // CSS for legend icon colors
    let legendIconStyle = '<style>';
    const allCategories = [...this.workTypeOrder, ...this.ratingOrder, 'unallocated'];
    allCategories.forEach(key => {
        const colorClass = this.colors[key] || 'bg-gray-300';
        if (colorClass.startsWith('bg-')) {
            const colorName = colorClass.substring(3, colorClass.lastIndexOf('-'));
            const colorShade = colorClass.substring(colorClass.lastIndexOf('-') + 1);
            if (colorName && colorShade) { // Ensure colorName and colorShade are valid
                 legendIconStyle += `.${colorClass.replace('bg-', 'text-')} .legend-icon svg { stroke: theme(colors.${colorName}.${colorShade}); } `;
            }
        } else if (colorClass.includes('text-')) { // For unallocated specifically if it's like 'bg-gray-200 text-gray-700'
            const textC = colorClass.split(' ').find(c => c.startsWith('text-'));
            if (textC) {
                const colorName = textC.substring(5, textC.lastIndexOf('-'));
                const colorShade = textC.substring(textC.lastIndexOf('-') + 1);
                if (colorName && colorShade) {
                    legendIconStyle += `.${textC} .legend-icon svg { stroke: theme(colors.${colorName}.${colorShade}); } `;
                }
            }
        }
    });
    legendIconStyle += '.bar-icon svg { stroke: white; }'; // Bar icons are always white
    legendIconStyle += '</style>';


    this.container.innerHTML = `
      <div id="team-visualization-section" class="bg-white p-4 rounded-lg shadow mb-6" style="display: none;">
        <h2 class="text-lg font-medium text-gray-900 mb-4">My Team's Work Map</h2>
        ${createBarSectionHTML('team-type', "Team Work Distribution by Type", this.workTypeOrder)}
        ${createBarSectionHTML('team-rating', "Team Work Distribution by Engagement", this.ratingOrder)}
      </div>
      <div id="personal-visualization-section" class="bg-white p-4 rounded-lg shadow mb-6">
        <h2 class="text-lg font-medium text-gray-900 mb-4">${this.personalSectionTitle}</h2>
        ${createBarSectionHTML('personal-type', "Work Distribution by Type", this.workTypeOrder)}
        ${createBarSectionHTML('personal-rating', "Work Distribution by Engagement", this.ratingOrder)}
      </div>
      ${legendIconStyle}
    `;

    this.attachEventListeners();
  }

  private attachEventListeners(): void {
    const barSegments = this.container.querySelectorAll('.bar-segment');
    barSegments.forEach(segment => {
      const htmlSegment = segment as HTMLElement;
      htmlSegment.addEventListener('mouseenter', (event) => this.showTooltip(event, htmlSegment));
      htmlSegment.addEventListener('mousemove', (event) => this.moveTooltip(event));
      htmlSegment.addEventListener('mouseleave', () => this.hideTooltip());

      // Add click handler for non-unallocated segments
      if (!htmlSegment.id.includes('unallocated') && this.onBarClick !== null) {
        htmlSegment.style.cursor = 'pointer';
        htmlSegment.addEventListener('click', () => {
          const categoryMatch = htmlSegment.id.match(/(personal|team)-(type|rating)-(\w+)-bar/);
          if (categoryMatch) {
            const [, , category, type] = categoryMatch;
            if (this.onBarClick) {
              this.onBarClick(type, category === 'type' ? 'work_type' : 'rating');
            }
          }
        });
      }
    });
  }

  private showTooltip(event: MouseEvent, segment: HTMLElement): void {
    if (!this.tooltipElement) return;

    const categoryName = segment.dataset.categoryName || 'N/A';
    // The actual hours value will be dynamically updated in updateDisplay, so we retrieve it from the title attribute for the tooltip
    const titleAttr = segment.getAttribute('title') || '';
    const hoursMatch = titleAttr.match(/: (\d+\.?\d*|\d+) hours/);
    const hours = hoursMatch ? parseFloat(hoursMatch[1]) : 0;
    
    // Determine which section this belongs to and get the appropriate total capacity
    const isTeamSection = segment.id.startsWith('team-');
    const totalCapacityHours = isTeamSection ? this.teamMaxMonthlyHours : this.personalMaxMonthlyHours;
    
    // Calculate percentage of total capacity
    const percentage = totalCapacityHours > 0 ? (hours / totalCapacityHours) * 100 : 0;
    
    this.tooltipElement.textContent = `${categoryName}: ${Math.round(hours)} hours (${Math.round(percentage)}%)`;
    
    // Apply colors
    const colorClasses = segment.dataset.colorClass?.split(' ') || ['bg-gray-900', 'text-white'];
    const bgColorClass = colorClasses.find(c => c.startsWith('bg-')) || 'bg-gray-900';
    // Determine text color for good contrast. Default to white, switch to dark for light backgrounds.
    let textColorClass = 'text-white';
    if (bgColorClass.includes('gray-200') || bgColorClass.includes('yellow-500') || bgColorClass.includes('green-500')) { // Add more light bg colors if needed
        // For light backgrounds, we might want dark text if the segment text color isn't already set for contrast.
        // The unallocated bar already has text-gray-700, so we can check for explicit text color
        const segmentTextColorClass = colorClasses.find(c => c.startsWith('text-'));
        if(segmentTextColorClass && (segmentTextColorClass.includes('gray-700') || segmentTextColorClass.includes('black'))) {
            textColorClass = segmentTextColorClass; // Use the segment's defined contrasting text color
        } else if (!segmentTextColorClass && (bgColorClass.includes('gray-200') || bgColorClass.includes('yellow-400') || bgColorClass.includes('yellow-300'))) {
             textColorClass = 'text-gray-700'; // Default dark text for light BGs without explicit text color
        }
    }
    
    this.tooltipElement.className = `absolute z-50 px-3 py-1.5 text-sm font-medium rounded-md shadow-lg opacity-90 pointer-events-none ${bgColorClass} ${textColorClass}`;
    
    this.tooltipElement.style.display = 'block';
    this.moveTooltip(event); // Initial position
  }

  private moveTooltip(event: MouseEvent): void {
    if (!this.tooltipElement || this.tooltipElement.style.display === 'none') return;

    // Position tooltip near the cursor
    // Add small offset to prevent flickering if the tooltip itself triggers a mousemove
    let x = event.pageX + 15;
    let y = event.pageY + 15;

    // Keep tooltip within viewport
    if (this.tooltipElement) {
        const tooltipRect = this.tooltipElement.getBoundingClientRect();
        if (x + tooltipRect.width > window.innerWidth) {
            x = event.pageX - tooltipRect.width - 15;
        }
        if (y + tooltipRect.height > window.innerHeight) {
            y = event.pageY - tooltipRect.height - 15;
        }
    }

    this.tooltipElement.style.left = `${x}px`;
    this.tooltipElement.style.top = `${y}px`;
  }

  private hideTooltip(): void {
    if (this.tooltipElement) {
      this.tooltipElement.style.display = 'none';
    }
  }

  private calculateMonthlyHours(work: WorkItem, weeklyHours: number = 40): number {
    const hoursPerTask = parseFloat(work.time_per_task as any);
    if (isNaN(hoursPerTask)) {
      console.warn('Invalid time_per_task for work item:', work);
      return 0;
    }
    const monthlyMultiplier = (weeklyHours / 40) * 4.3; // Scale based on weekly hours

    switch (work.frequency.toLowerCase()) {
      case 'daily':
        return hoursPerTask * 5 * monthlyMultiplier; // 5 workdays * monthlyMultiplier
      case 'weekly':
        return hoursPerTask * monthlyMultiplier; // monthlyMultiplier weeks per month
      case 'monthly':
        return hoursPerTask; // monthly tasks are not scaled by weekly hours in this model
      case 'quarterly':
        return hoursPerTask / 3;
      case 'ad-hoc':
        return hoursPerTask; // treat ad-hoc as monthly
      default:
        return 0;
    }
  }

  public update(works: WorkItem[], teamWorks: WorkItem[] = [], snapshotUsers: SnapshotUser[] = []): void {
    this.data = { total: 0, byType: {}, byRating: {} };
    this.teamData = { total: 0, byType: {}, byRating: {} };
    
    const userHoursMap = new Map<number, number>();
    snapshotUsers.forEach(su => {
      userHoursMap.set(su.user_id, su.weekly_hours);
    });

    this.personalMaxMonthlyHours = 0;
    const personalUserIds = new Set<number>();
    works.forEach(work => {
      if (work.user_id) personalUserIds.add(work.user_id);
      const weeklyHours = work.user_id ? userHoursMap.get(work.user_id) : 40;
      const monthlyHours = this.calculateMonthlyHours(work, weeklyHours);
      this.data.total += monthlyHours;
      this.data.byType[work.work_type] = (this.data.byType[work.work_type] || 0) + monthlyHours;
      this.data.byRating[work.rating] = (this.data.byRating[work.rating] || 0) + monthlyHours;
    });

    personalUserIds.forEach(userId => {
        const weeklyHours = userHoursMap.get(userId) || 40;
        this.personalMaxMonthlyHours += weeklyHours * 4.3;
    });

    if (personalUserIds.size === 0 && works.length > 0) { // If user_id is not present, assume 1 user for personal
        this.personalMaxMonthlyHours = 40 * 4.3;
    }
    if (works.length === 0 && personalUserIds.size === 0) {
        // Use the first snapshot user's weekly hours, or fall back to 40 if none exists
        const firstUser = snapshotUsers[0];
        const weeklyHours = firstUser ? firstUser.weekly_hours : 40;
        this.personalMaxMonthlyHours = weeklyHours * 4.3;
    }

    this.teamMaxMonthlyHours = 0;
    const teamUserIds = new Set<number>();
    teamWorks.forEach(work => {
      if (work.user_id) teamUserIds.add(work.user_id);
      const weeklyHours = work.user_id ? userHoursMap.get(work.user_id) : 40;
      const monthlyHours = this.calculateMonthlyHours(work, weeklyHours);
      this.teamData.total += monthlyHours;
      this.teamData.byType[work.work_type] = (this.teamData.byType[work.work_type] || 0) + monthlyHours;
      this.teamData.byRating[work.rating] = (this.teamData.byRating[work.rating] || 0) + monthlyHours;
    });

    const processedTeamUserIds = new Set<number>();
    teamWorks.forEach(work => {
        if(work.user_id && !processedTeamUserIds.has(work.user_id)) {
            const weeklyHours = userHoursMap.get(work.user_id) || 40;
            this.teamMaxMonthlyHours += weeklyHours * 4.3;
            processedTeamUserIds.add(work.user_id);
        }
    });
    
    if (processedTeamUserIds.size === 0 && teamWorks.length > 0) {
        this.teamMaxMonthlyHours = 40 * 4.3;
    }
    if (teamWorks.length === 0) this.teamMaxMonthlyHours = 0;

    this.updateDisplay();
  }

  private updateDisplay(): void {
    const updateBarElements = (
      sectionPrefix: string, 
      sourceData: Record<string, number>, 
      totalLoggedHours: number, 
      maxCapacityHours: number, // Changed from totalHours to maxCapacityHours
      categoryOrder: string[]
    ) => {
      let currentOffsetPercentage = 0;
      const barContainerWidth = (this.container.querySelector(`#${sectionPrefix}-bar-container`) as HTMLElement)?.offsetWidth || 1;


      categoryOrder.forEach(key => {
        const hours = sourceData[key] || 0;
        const percentage = maxCapacityHours > 0 ? (hours / maxCapacityHours) * 100 : 0;
        const barElement = document.getElementById(`${sectionPrefix}-${key}-bar`) as HTMLElement;
        const legendHoursElement = document.getElementById(`${sectionPrefix}-${key}-legend-hours`) as HTMLElement;
        const iconElement = barElement.querySelector('.bar-icon') as HTMLElement;

        if (barElement) {
          barElement.style.width = `${percentage}%`;
          barElement.title = `${key}: ${Math.round(hours)} hours`;
          // Min width to show icon (e.g. 20px)
          const minPixelWidthForIcon = 20;
          const segmentPixelWidth = (percentage / 100) * barContainerWidth;
          if (iconElement) iconElement.style.display = segmentPixelWidth >= minPixelWidthForIcon && hours > 0 ? 'inline-block' : 'none';
           barElement.style.display = hours > 0 ? 'flex': 'none'; // Hide bar if 0 hours
        }
        if (legendHoursElement) {
          legendHoursElement.textContent = Math.round(hours).toString();
        }
        currentOffsetPercentage += percentage;
      });

      const unallocatedHours = Math.max(0, maxCapacityHours - totalLoggedHours);
      const unallocatedPercentage = maxCapacityHours > 0 ? (unallocatedHours / maxCapacityHours) * 100 : (totalLoggedHours === 0 ? 100 : 0);
      const unallocatedBarElement = document.getElementById(`${sectionPrefix}-unallocated-bar`) as HTMLElement;
      const unallocatedLegendHoursElement = document.getElementById(`${sectionPrefix}-unallocated-legend-hours`) as HTMLElement;
      
      if (unallocatedBarElement) {
        unallocatedBarElement.style.width = `${unallocatedPercentage}%`;
        unallocatedBarElement.title = `Unallocated: ${Math.round(unallocatedHours)} hours`;
        const iconElement = unallocatedBarElement.querySelector('.bar-icon') as HTMLElement;
        const segmentPixelWidth = (unallocatedPercentage / 100) * barContainerWidth;
        const minPixelWidthForIcon = 20;
         if (iconElement) iconElement.style.display = segmentPixelWidth >= minPixelWidthForIcon && unallocatedHours > 0 ? 'inline-block' : 'none';
         unallocatedBarElement.style.display = unallocatedHours > 0 || totalLoggedHours === 0 ? 'flex' : 'none'; // Show if unallocated or if total is 0
      }
      if (unallocatedLegendHoursElement) {
        unallocatedLegendHoursElement.textContent = Math.round(unallocatedHours).toString();
      }
      
      const totalHoursDisplayElement = document.getElementById(`${sectionPrefix}-total-hours`) as HTMLElement;
      if (totalHoursDisplayElement) {
        totalHoursDisplayElement.textContent = `${Math.round(totalLoggedHours)} / ${Math.round(maxCapacityHours)}`;
      }
    };

    // Update Personal Bars
    updateBarElements('personal-type', this.data.byType, this.data.total, this.personalMaxMonthlyHours, this.workTypeOrder);
    updateBarElements('personal-rating', this.data.byRating, this.data.total, this.personalMaxMonthlyHours, this.ratingOrder);

    // Update Team Bars and visibility
    const teamSection = document.getElementById('team-visualization-section') as HTMLElement;
    if (teamSection) {
      if (this.teamData.total > 0 || this.teamMaxMonthlyHours > 0) { // Show if there's data or capacity
        teamSection.style.display = 'block';
        updateBarElements('team-type', this.teamData.byType, this.teamData.total, this.teamMaxMonthlyHours, this.workTypeOrder);
        updateBarElements('team-rating', this.teamData.byRating, this.teamData.total, this.teamMaxMonthlyHours, this.ratingOrder);
      } else {
        teamSection.style.display = 'none';
      }
    }
  }
} 
